/**
 * Client-side mirror of the server's profile-field policy
 * (api/util/profileFields.js).
 *
 * The forms used to validate names against `^[a-zA-Z0-9]+$`, which is far
 * stricter than anything the server enforces and rejects a great many real
 * names: O'Brien, Van Dyke, Smith-Jones, Jose', Muller, and any name not written
 * in ASCII at all. That mattered most for single-sign-on users, whose names
 * arrive from the identity provider and are perfectly acceptable to the server,
 * yet could not be saved or corrected from the admin form.
 *
 * The server's policy is a deny-list, for the reason recorded in its header: an
 * allow-list excludes legitimate users. This keeps the two in step - cap the
 * length, reject character categories that have no place in a display name, and
 * accept everything else.
 *
 * Written as explicit code-point ranges rather than \p{...} escapes so it does
 * not depend on Unicode property support in the build toolchain:
 *
 *    -, -  control characters, incl. CR/LF
 *   ­                        soft hyphen (invisible)
 *   ​-‏                 zero-width characters and LTR/RTL marks
 *    ,                  line and paragraph separators
 *   ‪-‮                 bidirectional overrides (display spoofing)
 *   ⁠-⁤, ⁦-⁯  invisible operators and bidi isolates
 *   \uD800-\uDFFF                 lone surrogates
 *   -                 private use area
 *   ﻿                        byte order mark
 */

// Keep in step with PROFILE_LIMITS.name on the server.
export const NAME_MAX_LENGTH = 64;

const UNSAFE_DISPLAY_CHARS = new RegExp(
	'[\\u0000-\\u001F\\u007F-\\u009F\\u00AD\\u200B-\\u200F\\u2028\\u2029' +
	'\\u202A-\\u202E\\u2060-\\u2064\\u2066-\\u206F\\uD800-\\uDFFF' +
	'\\uE000-\\uF8FF\\uFEFF]'
);

/**
 * True when the value is safe to store and display as a name.
 * Empty is safe - `required` is a separate concern.
 */
export const isSafeDisplayText = value => {
	if (value === null || value === undefined || value === '') return true;
	return !UNSAFE_DISPLAY_CHARS.test(String(value));
};

/**
 * Formsy validation object for a display-name field. Use with
 * NAME_VALIDATION_ERRORS:
 *
 *   validations={NAME_VALIDATIONS}
 *   validationErrors={NAME_VALIDATION_ERRORS('First name')}
 */
export const NAME_VALIDATIONS = {
	safeDisplayText: (values, value) => isSafeDisplayText(value),
	maxLength: NAME_MAX_LENGTH,
};

export const NAME_VALIDATION_ERRORS = field => ({
	safeDisplayText: `${field} contains characters that are not allowed.`,
	maxLength: `${field} cannot exceed ${NAME_MAX_LENGTH} characters.`,
});
